// Device fingerprint — the key the free-scan quota is counted against. IP is
// useless here (everyone's on 5G / CGNAT behind shared addresses), so we build a
// stable per-device id from properties that survive reloads and don't change
// between sessions on the same device: canvas + WebGL rendering, screen/DPR,
// timezone, language, CPU/RAM hints and the platform string. Hashed (in WASM) to
// a 64-hex id the server treats as opaque.
//
// This is a funnel key, not a hard identity: a determined user can spoof or reset
// it. The proof-of-work on session creation is what makes farming many fresh
// fingerprints expensive; this just gives the quota something to anchor to.

import { sha256Hex } from './signer';

const STORAGE_KEY = 'as.device.fp';

function canvasSignature(): string {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 240;
    canvas.height = 60;
    const ctx = canvas.getContext('2d');
    if (!ctx) return 'no-2d';
    ctx.textBaseline = 'top';
    ctx.font = "14px 'Arial'";
    ctx.fillStyle = '#f60';
    ctx.fillRect(2, 2, 120, 30);
    ctx.fillStyle = '#069';
    ctx.fillText('AutoSpy \u{1f697} fp', 4, 8);
    ctx.fillStyle = 'rgba(102,204,0,0.7)';
    ctx.fillText('AutoSpy \u{1f697} fp', 6, 18);
    return canvas.toDataURL();
  } catch {
    return 'canvas-err';
  }
}

function webglSignature(): string {
  try {
    const canvas = document.createElement('canvas');
    const gl = (canvas.getContext('webgl') || canvas.getContext('experimental-webgl')) as WebGLRenderingContext | null;
    if (!gl) return 'no-webgl';
    const dbg = gl.getExtension('WEBGL_debug_renderer_info');
    const vendor = dbg ? gl.getParameter(dbg.UNMASKED_VENDOR_WEBGL) : gl.getParameter(gl.VENDOR);
    const renderer = dbg ? gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL) : gl.getParameter(gl.RENDERER);
    return `${vendor}~${renderer}`;
  } catch {
    return 'webgl-err';
  }
}

function staticSignature(): string {
  const nav = navigator as Navigator & { deviceMemory?: number };
  const screenBits = [
    screen.width,
    screen.height,
    screen.colorDepth,
    window.devicePixelRatio || 1,
  ].join('x');
  let tz = '';
  try {
    tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch {
    tz = String(new Date().getTimezoneOffset());
  }
  return [
    nav.userAgent || '',
    nav.language || '',
    (nav.languages || []).join(','),
    nav.platform || '',
    nav.hardwareConcurrency || 0,
    nav.deviceMemory || 0,
    screenBits,
    tz,
  ].join('|');
}

let cached: Promise<string> | null = null;

/** Stable 64-hex device id. Cached in memory and localStorage for speed, but
 * recomputed deterministically so clearing storage yields the same value. */
export function getFingerprint(): Promise<string> {
  if (cached) return cached;
  cached = (async () => {
    const material = [staticSignature(), canvasSignature(), webglSignature()].join('||');
    const fp = await sha256Hex(material);
    try {
      localStorage.setItem(STORAGE_KEY, fp);
    } catch {
      /* private mode / storage disabled — the value is still deterministic */
    }
    return fp;
  })();
  return cached;
}
